import React, { useMemo } from 'react';

function MiniProjectionBar({ ingresos, egresos, max, modoOscuro }) {
  const ingPct = max > 0 ? Math.min(100, (ingresos / max) * 100) : 0;
  const egrPct = max > 0 ? Math.min(100, (egresos / max) * 100) : 0;
  const track = modoOscuro ? 'bg-gray-800' : 'bg-gray-100';
  return (
    <div className="flex items-center gap-2 w-full max-w-[140px]">
      <div className={`flex-1 h-1.5 rounded-full overflow-hidden ${track}`}>
        <div className="h-full bg-green-500 rounded-full" style={{ width: `${ingPct}%` }} />
      </div>
      <div className={`flex-1 h-1.5 rounded-full overflow-hidden ${track}`}>
        <div className="h-full bg-red-400 rounded-full" style={{ width: `${egrPct}%` }} />
      </div>
    </div>
  );
}

/** Montos en tabla: sin centavos para que no dominen visualmente. */
function formatAmountDense(n, formatAmount) {
  const v = Number(n);
  if (!Number.isFinite(v)) return formatAmount(n);
  const full = formatAmount(v);
  if (typeof full === 'string') {
    const trimmed = full.replace(/,\d{2}(?!\d)/, '');
    if (trimmed !== full) return trimmed;
  }
  return formatSupuesto(v);
}

function AmountCell({ amount, lines, formatAmount, totalClass, subClass }) {
  return (
    <div>
      <div className={totalClass}>{formatAmountDense(amount, formatAmount)}</div>
      {lines?.length > 0 && (
        <div className={`text-[10px] leading-snug mt-0.5 font-normal ${subClass}`}>
          {lines.join(' · ')}
        </div>
      )}
    </div>
  );
}

function ingresoLines(m, formatDetail) {
  const lines = [];
  if (m.ingresosCobranza > 0) lines.push(`impagas ${formatDetail(m.ingresosCobranza)}`);
  if (m.ingresosRecurrentes > 0) lines.push(`rec. ${formatDetail(m.ingresosRecurrentes)}`);
  if (!lines.length) lines.push('sin ingresos previstos');
  return lines;
}

function egresoLines(m, formatDetail) {
  const lines = [];
  if (m.egresosPromedio > 0) lines.push(`prom. ${formatDetail(m.egresosPromedio)}`);
  if (m.egresosRecurrentes > 0) lines.push(`rec. ${formatDetail(m.egresosRecurrentes)}`);
  if (!lines.length) lines.push('sin egresos previstos');
  return lines;
}

export function ProjectedCashflowIntro({ proyeccion, modoOscuro, textMuted, divide }) {
  if (!proyeccion) return null;
  const s = proyeccion.supuestos || {};
  const box = modoOscuro ? 'bg-gray-800/40' : 'bg-sky-50/80';
  const body = modoOscuro ? 'text-gray-400' : 'text-gray-600';
  const emph = modoOscuro ? 'font-semibold text-gray-300' : 'font-semibold text-gray-800';

  return (
    <div className={`px-3 py-2.5 border-b text-[11px] leading-snug space-y-1.5 ${divide} ${box} ${body}`}>
      <p>
        <span className={emph}>No es el mismo número que “Flujo del mes”.</span>
        {' '}Acá se proyecta hacia adelante: cobro esperado de <span className={emph}>impagas</span> (por vencimiento × tasa histórica)
        más <span className={emph}>ventas recurrentes</span>. No incluye lo <span className={emph}>ya cobrado</span> ni ventas nuevas sin recurrente.
      </p>
      <p>
        Egresos: <span className={emph}>promedio de los últimos 3 meses</span> (repitiendo en cada mes proyectado)
        más <span className={emph}>gastos recurrentes</span>.
      </p>
      <ul className={`list-disc pl-4 space-y-0.5 text-[11px] ${textMuted}`}>
        <li>Tasa de cobranza histórica: {s.tasaCobranzaHistorica ?? proyeccion.tasaCobranzaHistorica}%</li>
        {(s.ingresoRecurrenteMensual || 0) > 0 && (
          <li>Ventas recurrentes: {formatSupuesto(s.ingresoRecurrenteMensual)} / mes</li>
        )}
        <li>
          Egresos base: prom. {formatSupuesto(s.egresoPromedioMensual)}
          {(s.egresoRecurrenteMensual || 0) > 0
            ? ` + recurrentes ${formatSupuesto(s.egresoRecurrenteMensual)} / mes`
            : ' (sin recurrentes de egreso)'}
        </li>
      </ul>
    </div>
  );
}

function formatSupuesto(n) {
  const v = Number(n) || 0;
  return new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS', maximumFractionDigits: 0 }).format(v);
}

/**
 * Tabla / lista de meses proyectados (flujo de caja Profesional+).
 */
export default function ProjectedCashflowPanel({
  proyeccion,
  formatAmount = formatSupuesto,
  modoOscuro,
  divide,
  textSec,
  textMuted,
  showMiniBar = false,
}) {
  const meses = useMemo(
    () => proyeccion?.mesesProyectados ?? [],
    [proyeccion?.mesesProyectados],
  );
  const proyMax = useMemo(
    () => Math.max(1, ...meses.map((m) => Math.max(m.ingresosEsperados || 0, m.egresosEsperados || 0))),
    [meses],
  );

  const green = modoOscuro ? 'text-green-400/90' : 'text-green-700';
  const red = modoOscuro ? 'text-red-400/90' : 'text-red-700';
  const amountCell = 'text-xs font-normal tabular-nums';

  if (!meses.length) {
    return (
      <p className={`text-xs px-3 py-6 text-center ${textMuted}`}>
        Sin datos suficientes para proyectar el flujo.
      </p>
    );
  }

  return (
    <>
      <ProjectedCashflowIntro
        proyeccion={proyeccion}
        modoOscuro={modoOscuro}
        textMuted={textMuted}
        divide={divide}
      />
      <p className={`text-[11px] px-3 py-1.5 border-b ${divide} ${textMuted}`}>
        Escenario base orientativo · {proyeccion.meses} mes(es)
      </p>

      <div className="hidden sm:block overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className={`text-[10px] uppercase tracking-wide ${textMuted} ${modoOscuro ? 'bg-gray-800/60' : 'bg-gray-50'}`}>
              <th className="text-left font-semibold py-2 px-3">Mes</th>
              <th className="text-right font-semibold py-2 px-2">Ingresos</th>
              <th className="text-right font-semibold py-2 px-2">Egresos</th>
              <th className="text-right font-semibold py-2 px-2">Flujo</th>
              <th className="text-right font-semibold py-2 px-3">Acum.</th>
              {showMiniBar && <th className="py-2 px-2 w-[150px]" aria-hidden />}
            </tr>
          </thead>
          <tbody>
            {meses.map((m) => (
              <tr key={m.mes} className={`border-t ${divide}`}>
                <td className={`py-2 px-3 text-xs font-medium capitalize ${textSec}`}>{m.label}</td>
                <td className={`py-2 px-2 text-right ${green}`}>
                  <AmountCell
                    amount={m.ingresosEsperados}
                    lines={ingresoLines(m, formatSupuesto)}
                    formatAmount={formatAmount}
                    totalClass={amountCell}
                    subClass={textMuted}
                  />
                </td>
                <td className={`py-2 px-2 text-right ${red}`}>
                  <AmountCell
                    amount={m.egresosEsperados}
                    lines={egresoLines(m, formatSupuesto)}
                    formatAmount={formatAmount}
                    totalClass={amountCell}
                    subClass={textMuted}
                  />
                </td>
                <td className={`py-2 px-2 text-right ${amountCell} font-medium ${
                  m.flujoProyectado >= 0 ? green : red
                }`}>
                  {formatAmountDense(m.flujoProyectado, formatAmount)}
                </td>
                <td className={`py-2 px-3 text-right ${amountCell} ${textSec}`}>
                  {formatAmountDense(m.acumulado, formatAmount)}
                </td>
                {showMiniBar && (
                  <td className="py-2 px-2">
                    <MiniProjectionBar
                      ingresos={m.ingresosEsperados}
                      egresos={m.egresosEsperados}
                      max={proyMax}
                      modoOscuro={modoOscuro}
                    />
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <ul className={`sm:hidden divide-y ${divide}`}>
        {meses.map((m) => (
          <li key={m.mes} className="px-3 py-2.5">
            <div className="flex items-center justify-between gap-2">
              <span className={`text-xs font-medium capitalize ${textSec}`}>{m.label}</span>
              <span className={`text-xs font-medium tabular-nums ${m.flujoProyectado >= 0 ? green : red}`}>
                {formatAmountDense(m.flujoProyectado, formatAmount)}
              </span>
            </div>
            <div className={`mt-1 space-y-0.5 text-xs ${textMuted}`}>
              <p>
                Ing. <span className="tabular-nums">{formatAmountDense(m.ingresosEsperados, formatAmount)}</span>
                <span className="block text-[10px] font-normal">{ingresoLines(m, formatSupuesto).join(' · ')}</span>
              </p>
              <p>
                Egr. <span className="tabular-nums">{formatAmountDense(m.egresosEsperados, formatAmount)}</span>
                <span className="block text-[10px] font-normal">{egresoLines(m, formatSupuesto).join(' · ')}</span>
              </p>
              <p className="tabular-nums">Acum. {formatAmountDense(m.acumulado, formatAmount)}</p>
            </div>
            {showMiniBar && (
              <div className="mt-2">
                <MiniProjectionBar
                  ingresos={m.ingresosEsperados}
                  egresos={m.egresosEsperados}
                  max={proyMax}
                  modoOscuro={modoOscuro}
                />
              </div>
            )}
          </li>
        ))}
      </ul>
    </>
  );
}
